import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardPage from "@/pages/DashboardPage";
import PackagesPage from "@/pages/PackagesPage";
import TrackingPage from "@/pages/TrackingPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import PricingPage from "@/pages/PricingPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import OnboardingPage from "@/pages/OnboardingPage";
import NotFoundPage from "@/pages/NotFoundPage";

// Components
import { ToastProvider, Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/hooks/useAuth";
import AuthGuard from "@/components/AuthGuard";

// Reset onboarding for testing purposes - you can call this from console
if (import.meta.env.DEV) {
  (window as any).resetOnboarding = () => {
    localStorage.removeItem("shopglobal_onboarding_complete");
    console.log(
      "🔄 Onboarding completion status reset - you can now redo onboarding"
    );
    window.location.href = "/onboarding"; // Redirect to onboarding
  };

  console.log(
    "🛠️  Test utility available in console: window.resetOnboarding()"
  );
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <Router
            future={{
              v7_relativeSplatPath: true,
            }}>
            <div className="min-h-screen">
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/onboarding" element={
                  <AuthGuard requireAuth={true}>
                    <OnboardingPage />
                  </AuthGuard>
                } />
                <Route
                  path="/onboarding/success"
                  element={
                    <AuthGuard requireAuth={true}>
                      <OnboardingPage />
                    </AuthGuard>
                  }
                />
                <Route path="/dashboard" element={
                  <AuthGuard requireAuth={true}>
                    <DashboardPage />
                  </AuthGuard>
                } />
                <Route path="/packages" element={
                  <AuthGuard requireAuth={true}>
                    <PackagesPage />
                  </AuthGuard>
                } />
                <Route path="/tracking" element={
                  <AuthGuard requireAuth={true}>
                    <TrackingPage />
                  </AuthGuard>
                } />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/pricing" element={<PricingPage />} />

                {/* Warehouse Admin Routes */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route
                  path="/admin/dashboard"
                  element={
                    <AuthGuard requireAuth={true} allowedRoles={["admin"]}>
                      <AdminDashboardPage />
                    </AuthGuard>
                  }
                />

                {/* Catch-all route for 404 */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <Toaster />
            </div>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
